/**
 * Contract generations and session-key exposure: pure mappers for Wallet 360's generation badge
 * and Keys panel, and for the fleet page.
 *
 * A generation is one deployment of the `RestrictedECDSAValidator` + Main / Reserve
 * `WithdrawalCooldownHook` tuple. It is baked into the accounts' CREATE2 salt, so a wallet keeps
 * its generation for life and only changes it by migrating to a successor wallet. Two coexist:
 *   - v3.7: every wallet before the v3.8 flip. Its validator's default-allow fall-through lets the
 *     owner key re-key an installed session-key permission, and every key enabled before the
 *     permission pin (PR-1) answers ERC-1271 — a compromised backend could sign the account's USDC
 *     away with an EIP-3009 `transferWithAuthorization`.
 *   - v3.8: deployed 2026-11-04; default-deny owner path. New wallets after the flip.
 *
 * Contract source: elios-backend `domains/wallet/CLAUDE.md` § Contract generations and
 * § Operator key inventory, `firestore/session-key-permission.yaml`, `wallet-key-inventory.yaml`.
 */
import type { MantineColor } from "@mantine/core";
import type {
  SessionKeyPermissionDoc,
  WalletAvailabilityDoc,
  WalletKeyInventoryDoc,
  WalletKeyInventoryKey,
  WithDocId,
} from "@/firestore/types";

// ───────────────────────────── generations ─────────────────────────────

export interface RegisteredGeneration {
  key: string;
  /** Every wallet on a deprecated generation must migrate. */
  deprecated: boolean;
}

/** The backend's `buildBuiltInGenerations()`, oldest first. Keys are labels; never compare them. */
export const REGISTERED_GENERATIONS: readonly RegisteredGeneration[] = [
  { key: "v3.5", deprecated: true },
  { key: "v3.6", deprecated: true },
  { key: "v3.7", deprecated: false },
  { key: "v3.8", deprecated: false },
];

/** The generation that closes the cooldown bypass. Wallets on any other generation can migrate to it. */
export const FIXED_GENERATION = "v3.8";

export type GenerationTone = "fixed" | "legacy" | "deprecated" | "unregistered" | "unknown";

export interface GenerationDescriptor {
  /** The raw value, or null when absent. */
  key: string | null;
  label: string;
  tone: GenerationTone;
  color: MantineColor;
  description: string;
}

const UNKNOWN_DESCRIPTION =
  "Not projected yet: the wallet document predates wallet-service 0.74.0 and the backfill has " +
  "not rewritten it. The backend still knows the wallet's generation.";

/** Badge text, colour and tooltip for a wallet's `contractGeneration`. Never throws. */
export function describeGeneration(value: unknown): GenerationDescriptor {
  if (typeof value !== "string" || value.trim() === "") {
    return {
      key: null,
      label: "Generation unknown",
      tone: "unknown",
      color: "gray",
      description: UNKNOWN_DESCRIPTION,
    };
  }
  const key = value.trim();
  if (key === FIXED_GENERATION) {
    return {
      key,
      label: key,
      tone: "fixed",
      color: "green",
      description:
        "Cooldown-bypass fix: the validator default-denies owner-key calls to unregistered " +
        "targets, and new session keys are pinned to user operations only (no ERC-1271).",
    };
  }
  const registered = REGISTERED_GENERATIONS.find((g) => g.key === key);
  if (!registered) {
    return {
      key,
      label: key,
      tone: "unregistered",
      color: "gray",
      description: "Not a generation this console knows. Check the backend's generation registry.",
    };
  }
  if (registered.deprecated) {
    return {
      key,
      label: key,
      tone: "deprecated",
      color: "red",
      description: `Deprecated generation: the wallet must migrate before it can act again (409 WALLET_MIGRATION_REQUIRED).`,
    };
  }
  return {
    key,
    label: key,
    tone: "legacy",
    color: "orange",
    description:
      "Pre-fix generation: the owner key can re-key an installed session-key permission, and keys " +
      "enabled before the permission pin answer ERC-1271. Migrate to v3.8 case by case.",
  };
}

/** Whether a wallet on this generation would benefit from a migration to {@link FIXED_GENERATION}. */
export function canOfferFixedGeneration(value: unknown): boolean {
  const tone = describeGeneration(value).tone;
  return tone === "legacy" || tone === "deprecated";
}

// ───────────────────────────── permission flags ─────────────────────────────

/** Kernel `PassFlag` bit that skips the permission on the ERC-1271 path (`NOT_FOR_VALIDATE_SIG`). */
export const NOT_FOR_VALIDATE_SIG = 0x0002;

/**
 * Whether a permission with this flag answers ERC-1271: it lacks the `0x0002` bit. Null / absent
 * is a legacy key (read as `0x0000`). An unparseable flag is treated as capable — the unsafe
 * reading of an unknown value is the one that gets looked at.
 */
export function flagAnswersErc1271(flag: string | null | undefined): boolean {
  if (flag === null || flag === undefined || flag === "") return true;
  const parsed = Number.parseInt(flag, 16);
  if (!/^0x[0-9a-f]+$/i.test(flag) || Number.isNaN(parsed)) return true;
  return (parsed & NOT_FOR_VALIDATE_SIG) === 0;
}

type PermissionLike = Partial<
  Pick<
    SessionKeyPermissionDoc,
    | "legacySignatureCapable"
    | "enabledOnChainAt"
    | "permissionFlag"
    | "state"
    | "revokedAt"
    | "kmsKeyDestroyedAt"
  >
>;

/**
 * Whether the key's permission itself answers ERC-1271: enabled on chain (`enabledOnChainAt`) with
 * a flag lacking the `0x0002` bit. True for life once installed (only a v3.8 migration removes the
 * permission); whether the key still EXPOSES the account also depends on its KMS material.
 */
export function permissionAnswersErc1271(key: PermissionLike): boolean {
  return !!key.enabledOnChainAt && flagAnswersErc1271(key.permissionFlag);
}

/**
 * The exposure verdict (`legacySignatureCapable`, wallet-service 0.74.0): the permission answers
 * ERC-1271 AND the key's KMS material is not scheduled for destruction, whatever the row's state.
 * A revoked legacy key is still an exposure (revocation is off-chain) until its destruction is
 * scheduled. A pending key never is (its pin is recorded at enable).
 *
 * The projected verdict counts; the console also recomputes the rule from the row's own fields and
 * flags the key when either says exposed, so an inconsistent or older projection errs towards the
 * unsafe reading. Inventory entries carry no `enabledOnChainAt` / `kmsKeyDestroyedAt`, so for
 * them only the projected verdict speaks.
 */
export function isLegacySignatureCapable(key: PermissionLike): boolean {
  if (key.legacySignatureCapable === true) return true;
  return permissionAnswersErc1271(key) && !key.kmsKeyDestroyedAt;
}

// ───────────────────────────── key exposure ─────────────────────────────

export type KeyExposureKind =
  | "legacyEnabled"
  | "legacyRevokedExposed"
  | "legacyDestroyScheduled"
  | "pinned"
  | "pending"
  | "revoked";

export interface KeyExposure {
  kind: KeyExposureKind;
  label: string;
  color: MantineColor;
  /** True when the key is an exposure: it can still sign an ERC-1271 authorization for the account. */
  signatureCapable: boolean;
  detail: string;
}

const EXPOSURE: Record<KeyExposureKind, Omit<KeyExposure, "kind">> = {
  legacyEnabled: {
    label: "Legacy, 1271-capable",
    color: "red",
    signatureCapable: true,
    detail:
      "Enabled before the permission pin: the permission answers ERC-1271 for the account, so " +
      "whoever can sign with this key (the backend included) can sign a transferWithAuthorization " +
      "for the account's USDC. Revoking it is off-chain only; migrating the wallet to v3.8 removes it.",
  },
  legacyRevokedExposed: {
    label: "Revoked, still signature-capable until KMS destruction",
    color: "red",
    signatureCapable: true,
    detail:
      "Revoked off-chain only: the permission is still installed and answers ERC-1271, and the " +
      "key's KMS material is not scheduled for destruction yet, so it can still sign for the " +
      "account's USDC. It stops being an exposure once its destruction is scheduled (the teardown " +
      "follows every revocation: if it lingers, check the key's KMS status).",
  },
  legacyDestroyScheduled: {
    label: "Legacy permission, KMS destruction scheduled",
    color: "yellow",
    signatureCapable: false,
    detail:
      "The permission still answers ERC-1271 on chain, but the key's KMS material is scheduled for " +
      "destruction, so it no longer counts as an exposure. The version stays restorable by a KMS " +
      "admin until KMS destroys it (24 h for one-off keys, up to 120 days for autonomous ones); " +
      "the key's KMS status shows the live destroyTime.",
  },
  pinned: {
    label: "User ops only",
    color: "green",
    signatureCapable: false,
    detail:
      "Pinned NOT_FOR_VALIDATE_SIG (0x0002): Kernel refuses this permission on its ERC-1271 path.",
  },
  pending: {
    label: "Pending",
    color: "gray",
    signatureCapable: false,
    detail: "Not enabled yet. Its flag and signer module are pinned when it is enabled.",
  },
  revoked: {
    label: "Revoked",
    color: "gray",
    signatureCapable: false,
    detail: "Revoked, and its permission never answered ERC-1271.",
  },
};

/** How exposed a session key is, for the Keys panel's warning chip. */
export function keyExposure(key: PermissionLike): KeyExposure {
  const revoked = key.state === "revoked" || !!key.revokedAt;
  let kind: KeyExposureKind;
  if (isLegacySignatureCapable(key)) kind = revoked ? "legacyRevokedExposed" : "legacyEnabled";
  else if (permissionAnswersErc1271(key) && key.kmsKeyDestroyedAt) kind = "legacyDestroyScheduled";
  else if (revoked) kind = "revoked";
  else if (key.state === "pending" || !key.enabledOnChainAt) kind = "pending";
  else kind = "pinned";
  return { kind, ...EXPOSURE[kind] };
}

export interface KeyInventorySummary {
  total: number;
  enabled: number;
  pending: number;
  revoked: number;
  /** Exposed keys whatever their state: what the backend's per-wallet count counts. */
  exposed: number;
  /** Of which enabled. */
  exposedEnabled: number;
  /** Of which revoked (off-chain) with their KMS material not yet scheduled for destruction. */
  exposedRevoked: number;
  /** Legacy permissions that no longer count: KMS destruction scheduled. */
  destroyScheduled: number;
}

export function summarizeKeys(keys: readonly PermissionLike[]): KeyInventorySummary {
  const summary: KeyInventorySummary = {
    total: keys.length,
    enabled: 0,
    pending: 0,
    revoked: 0,
    exposed: 0,
    exposedEnabled: 0,
    exposedRevoked: 0,
    destroyScheduled: 0,
  };
  for (const key of keys) {
    const exposure = keyExposure(key);
    if (key.state === "enabled") summary.enabled += 1;
    else if (key.state === "pending") summary.pending += 1;
    else if (key.state === "revoked") summary.revoked += 1;
    if (exposure.signatureCapable) summary.exposed += 1;
    if (exposure.kind === "legacyEnabled") summary.exposedEnabled += 1;
    else if (exposure.kind === "legacyRevokedExposed") summary.exposedRevoked += 1;
    else if (exposure.kind === "legacyDestroyScheduled") summary.destroyScheduled += 1;
  }
  return summary;
}

/** Human label for a permission flag cell. */
export function flagLabel(key: PermissionLike): string {
  if (key.permissionFlag) return key.permissionFlag;
  if (key.state === "pending" || !key.enabledOnChainAt) return "pinned at enable";
  return "none (legacy, 0x0000)";
}

// ───────────────────────────── inventories & fleet ─────────────────────────────

/**
 * The inventory's key entries: every `enabled` key plus every exposed (revoked, KMS material still
 * live) legacy key. `enabledKeys` is the name an earlier cut of wallet-service 0.74.0 projected
 * (enabled keys only); read as a fallback so such documents still render.
 */
export function inventoryKeys(
  inventory: Partial<Pick<WalletKeyInventoryDoc, "exposedOrEnabledKeys" | "enabledKeys">> | null,
): WalletKeyInventoryKey[] {
  if (!inventory) return [];
  if (Array.isArray(inventory.exposedOrEnabledKeys)) return inventory.exposedOrEnabledKeys;
  if (Array.isArray(inventory.enabledKeys)) return inventory.enabledKeys;
  return [];
}

/** Whether an inventory entry is `enabled` (entries of the earlier cut carry no state: all were). */
function entryEnabled(key: WalletKeyInventoryKey): boolean {
  return key.state === undefined || key.state === "enabled";
}

/** The inventory's exposed-key count (whatever their state), falling back to counting its entries. */
export function legacyKeyCount(
  inventory: Partial<WalletKeyInventoryDoc> | null | undefined,
): number {
  if (!inventory) return 0;
  if (typeof inventory.legacySignatureCapableKeyCount === "number")
    return inventory.legacySignatureCapableKeyCount;
  return inventoryKeys(inventory).filter((k) => isLegacySignatureCapable(k)).length;
}

/** Which of the wallet's accounts carry an exposed key. Unknown roles count as both. */
export function exposedRoles(inventory: Partial<WalletKeyInventoryDoc> | null | undefined): {
  main: boolean;
  reserve: boolean;
} {
  const legacy = inventoryKeys(inventory ?? null).filter((k) => isLegacySignatureCapable(k));
  if (legacy.length === 0) {
    // A count with no key array (or an unreadable one): assume both accounts are reachable.
    const assumeBoth = legacyKeyCount(inventory) > 0;
    return { main: assumeBoth, reserve: assumeBoth };
  }
  const unknown = legacy.some(
    (k) => k.targetSmartAccount !== "main" && k.targetSmartAccount !== "reserve",
  );
  return {
    main: unknown || legacy.some((k) => k.targetSmartAccount === "main"),
    reserve: unknown || legacy.some((k) => k.targetSmartAccount === "reserve"),
  };
}

export interface FleetWalletRow {
  id: string;
  inventory: WithDocId<WalletKeyInventoryDoc>;
  availability: WalletAvailabilityDoc | null;
  generation: string | null;
  /** `walletState`, or `closed` when the wallet was soft-deleted. */
  state: string | null;
  /** Exposed keys, whatever their state (`legacySignatureCapableKeyCount`). */
  legacyKeys: number;
  /** Of which revoked (still signature-capable until their KMS destruction is scheduled). */
  revokedExposedKeys: number;
  enabledKeys: number;
  legacyKeyTypes: string[];
  mainMicroUsdc: number | null;
  reserveMicroUsdc: number | null;
  spendableMicroUsdc: number | null;
  /**
   * USDC an exposed key could sign away: the balance of each account (Main / Reserve) that
   * carries one. Null without a balance snapshot.
   */
  exposedMicroUsdc: number | null;
  updatedAt: string | null;
}

function integerOrNull(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

/** Joins inventories to their `walletAvailability` snapshots (same document id). */
export function joinInventoryBalances(
  inventories: readonly WithDocId<WalletKeyInventoryDoc>[],
  availabilities: readonly WithDocId<WalletAvailabilityDoc>[],
): FleetWalletRow[] {
  const byId = new Map(availabilities.map((a) => [a._docId, a]));
  return inventories.map((inventory) => {
    const availability = byId.get(inventory._docId) ?? null;
    const main = integerOrNull(availability?.mainBalanceMicroUsdc);
    const reserve = integerOrNull(availability?.reserveBalanceMicroUsdc);
    const roles = exposedRoles(inventory);
    const legacyKeys = legacyKeyCount(inventory);
    let exposed: number | null = null;
    if (availability) {
      exposed = (roles.main ? (main ?? 0) : 0) + (roles.reserve ? (reserve ?? 0) : 0);
    }
    const keys = inventoryKeys(inventory);
    return {
      id: inventory._docId,
      inventory,
      availability,
      generation: inventory.contractGeneration ?? null,
      state: inventory.walletDeletedAt ? "closed" : (inventory.walletState ?? null),
      legacyKeys,
      revokedExposedKeys: keys.filter((k) => isLegacySignatureCapable(k) && !entryEnabled(k))
        .length,
      enabledKeys:
        typeof inventory.enabledKeyCount === "number"
          ? inventory.enabledKeyCount
          : keys.filter(entryEnabled).length,
      legacyKeyTypes: [
        ...new Set(keys.filter((k) => isLegacySignatureCapable(k)).map((k) => k.type)),
      ].sort(),
      mainMicroUsdc: main,
      reserveMicroUsdc: reserve,
      spendableMicroUsdc: integerOrNull(availability?.spendableMicroUsdc),
      exposedMicroUsdc: legacyKeys > 0 ? exposed : 0,
      updatedAt: inventory.updatedAt ?? null,
    };
  });
}

/** Legacy-key count desc (the server order), then exposed USDC desc, then id. */
export function sortByExposure(rows: readonly FleetWalletRow[]): FleetWalletRow[] {
  return [...rows].sort(
    (a, b) =>
      b.legacyKeys - a.legacyKeys ||
      (b.exposedMicroUsdc ?? -1) - (a.exposedMicroUsdc ?? -1) ||
      a.id.localeCompare(b.id),
  );
}

/** Sum of the known exposed amounts, and how many rows had no balance snapshot. */
export function totalExposure(rows: readonly FleetWalletRow[]): {
  exposedMicroUsdc: number;
  withoutBalance: number;
} {
  let exposed = 0;
  let missing = 0;
  for (const row of rows) {
    if (row.exposedMicroUsdc === null) missing += 1;
    else exposed += row.exposedMicroUsdc;
  }
  return { exposedMicroUsdc: exposed, withoutBalance: missing };
}

/** Firestore's `in` limit: 30 values per query. */
export const IN_QUERY_LIMIT = 30;

/** Unique, non-empty values split into `in`-sized batches (stable order). */
export function inBatches(
  values: readonly (string | null | undefined)[],
  size = IN_QUERY_LIMIT,
): string[][] {
  const unique = [...new Set(values.filter((v): v is string => typeof v === "string" && v !== ""))];
  const batches: string[][] = [];
  for (let i = 0; i < unique.length; i += size) batches.push(unique.slice(i, i + size));
  return batches;
}
